import { useEffect, useState, useRef } from "react";
import styles from "./MegaQuiz.module.css";
import QuizCard from "../components/Monthlyquiz/QuizCard";
import ResultScreen from "../components/Monthlyquiz/ResultScreen";
import { quiz_data, save_result } from "../http/http";
import { useDispatch, useSelector } from "react-redux";
import { useLocation, useNavigate } from "react-router-dom";
import {
  initQuiz,
  nextQuestion,
  addScore,
  tickTimer,
  setCompleted,
  resetQuiz,
} from "../store/slices/Megaquizslice"; // adjust path as needed

export default function MegaQuiz() {
  const location = useLocation();
  const navigate = useNavigate();
  const dispatch = useDispatch();

  const msisdn = useSelector((s) => s.persisted.msisdn);

  // ── Redux persisted state ──────────────────────────────────────────────────
  const questions   = useSelector((s) => s.persisted.megaQuiz.questions);
  const current     = useSelector((s) => s.persisted.megaQuiz.current);
  const score       = useSelector((s) => s.persisted.megaQuiz.score);
  const completed   = useSelector((s) => s.persisted.megaQuiz.completed);
  const timeLeft    = useSelector((s) => s.persisted.megaQuiz.timeLeft);
  // Prefer quizDate from Redux (persisted); fall back to location.state on first load
  const quizDateRedux = useSelector((s) => s.persisted.megaQuiz.quizDate);
  const quizDate    = quizDateRedux ?? location.state?.quizDate;
  // ──────────────────────────────────────────────────────────────────────────

  // selected is pure UI state — no need to persist it
  const [selected, setSelected] = useState(null);

  const timerRef  = useRef(null);
  const scoreRef  = useRef(score);
  scoreRef.current = score;

  // ── Submit quiz ────────────────────────────────────────────────────────────
  const submitQuiz = async () => {
    clearInterval(timerRef.current);
    const r = await save_result(
      msisdn,
      quizDate?.start_time,
      timeLeft,
      scoreRef.current
    );
    console.log(r);
    dispatch(setCompleted());
  };

  // ── Fetch questions only when Redux has none (first visit / after reset) ───
  useEffect(() => {
    if (questions.length === 0) {
      fetchData();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const fetchData = async () => {
    const data = await quiz_data();
    if (data?.data) {
      dispatch(
        initQuiz({
          questions: data.data,
          quizDate:  quizDate ?? null,
        })
      );
    }
  };

  // ── Timer ──────────────────────────────────────────────────────────────────
  useEffect(() => {
    if (completed) return;

    // If end_time has already passed, submit immediately
    if (quizDate?.end_time) {
      const endTime = new Date(quizDate.end_time).getTime();
      if (Date.now() >= endTime) {
        submitQuiz();
        return;
      }
    }

    timerRef.current = setInterval(() => {
      // Hard deadline from server takes priority
      if (quizDate?.end_time) {
        const endTime = new Date(quizDate.end_time).getTime();
        if (Date.now() >= endTime) {
          submitQuiz();
          return;
        }
      }

      // Local countdown — tick via Redux so it's persisted
      if (timeLeft <= 1) {
        submitQuiz();
        return;
      }
      dispatch(tickTimer());
    }, 1000);

    return () => clearInterval(timerRef.current);
    // Re-run only when quizDate or completed changes; timeLeft is read from Redux
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [quizDate, completed]);

  // ── Helpers ────────────────────────────────────────────────────────────────
  const formatTime = (seconds) => {
    const m = Math.floor(seconds / 60).toString().padStart(2, "0");
    const s = (seconds % 60).toString().padStart(2, "0");
    return `${m}:${s}`;
  };

  const isWarning = timeLeft <= 60;

  // ── Handle answer + advance ────────────────────────────────────────────────
  const handleNext = () => {
    if (selected === null) return;

    if (selected === currentQuestion.correctAnswer) {
      dispatch(addScore(5));
    }

    if (current === 24 || current === questions.length - 1) {
      submitQuiz();
      return;
    }

    dispatch(nextQuestion());
    setSelected(null);
  };

  // ── Cleanup Redux on unmount after completion ──────────────────────────────
  useEffect(() => {
    return () => {
      // Only reset when the quiz is done so a back-navigation doesn't wipe progress
      if (completed) dispatch(resetQuiz());
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [completed]);

  // ── Loading ────────────────────────────────────────────────────────────────
  if (!questions.length) {
    return <div className={styles.loader}>Loading Quiz...</div>;
  }

  const currentQuestion = questions[current];

  // ── Result screen ──────────────────────────────────────────────────────────
  if (completed) {
    return (
      <div
        style={{
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          justifyContent: "center",
          minHeight: "80vh",
          gap: "24px",
          padding: "20px",
        }}
      >
        <ResultScreen score={score} total={questions.length * 5} />
      </div>
    );
  }

  // ── Quiz UI ────────────────────────────────────────────────────────────────
  return (
    <div className={styles.container}>
      {/* Header */}
      <div className={styles.topBar}>
        <div className={styles.logoSection}>
          <span className={styles.logo}>Monthly Mega Quiz</span>
        </div>
        <div className={styles.scoreBadge}>🪙 Score: {score}</div>
      </div>

      {/* Timer */}
      <div
        style={{
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          gap: "8px",
          padding: "6px 16px",
          background: isWarning ? "#fff1f1" : "#f0f9ff",
          borderBottom: `2px solid ${isWarning ? "#f87171" : "#93c5fd"}`,
          fontWeight: 600,
          fontSize: "15px",
          color: isWarning ? "#dc2626" : "#1d4ed8",
          transition: "all 0.3s",
        }}
      >
        <span style={{ fontSize: "18px" }}>{isWarning ? "⚠️" : "⏱️"}</span>
        <span>
          {isWarning ? "Hurry! " : "Time Left: "}
          <span style={{ fontVariantNumeric: "tabular-nums" }}>
            {formatTime(timeLeft)}
          </span>
        </span>
      </div>

      {/* Question info */}
      <div className={styles.questionInfo}>
        <span>
          Question {current + 1} of {questions.length}
        </span>
        <span>
          {Math.round(((current + 1) / questions.length) * 100)}% Complete
        </span>
      </div>

      {/* Progress bar */}
      <div className={styles.progressBar}>
        <div
          className={styles.progressFill}
          style={{ width: `${((current + 1) / questions.length) * 100}%` }}
        />
      </div>

      {/* Question card */}
      <QuizCard
        question={currentQuestion}
        selected={selected}
        setSelected={setSelected}
        handleNext={handleNext}
      />

      {/* Footer */}
  
    </div>
  );
}